import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Calendar, 
  Trash2, 
  GripVertical,
  X,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { getTasks, createTask, updateTask, deleteTask } from '../services/kanbanApi';

// Types
export interface Task {
  id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'done';
  priority: 'low' | 'medium' | 'high';
  createdAt: number;
  dueDate?: number;
}

interface Column {
  id: 'todo' | 'in_progress' | 'done';
  title: string;
  color: string;
}

const columns: Column[] = [
  { id: 'todo', title: 'À faire', color: 'bg-[#5e5e5e]' },
  { id: 'in_progress', title: 'En cours', color: 'bg-[#1f75cb]' },
  { id: 'done', title: 'Terminé', color: 'bg-[#108548]' }
];

const priorityConfig = {
  low: { label: 'Basse', color: 'bg-gray-100 text-gray-600' },
  medium: { label: 'Moyenne', color: 'bg-[#fc6d26]/10 text-[#fc6d26]' },
  high: { label: 'Haute', color: 'bg-red-100 text-red-600' }
};

const STORAGE_KEY = 'kanban_tasks_backup';

export function AdminKanban() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<'todo' | 'in_progress' | 'done'>('todo');
  const [apiError, setApiError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  
  // Formulaire
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as Task['priority'],
    dueDate: ''
  });

  // Charger les tâches depuis l'API
  const loadTasks = useCallback(async () => {
    setIsLoading(true);
    setApiError(null);
    
    try {
      const tasksData = await getTasks();
      setTasks(tasksData);
      setIsOffline(false);
      // Backup dans localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tasksData));
    } catch (error) {
      console.error('Error loading tasks:', error);
      
      if (error instanceof Error && error.message === 'Firebase not configured') {
        setApiError('Le serveur de données n\'est pas configuré. Utilisation du mode hors ligne.');
      } else if (error instanceof Error && error.message === 'Unauthorized') {
        setApiError('Session expirée. Veuillez vous reconnecter.');
      } else {
        setApiError('Erreur de connexion au serveur. Mode hors ligne activé.');
      }
      
      setIsOffline(true);
      // Charger depuis localStorage en fallback
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          setTasks(JSON.parse(saved));
        } catch {
          setTasks([]);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Ajouter une tâche
  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    setIsSubmitting(true);

    const now = Date.now();
    const newTaskData = {
      title: formData.title,
      description: formData.description,
      status: selectedColumn,
      priority: formData.priority,
      createdAt: now,
      dueDate: formData.dueDate ? new Date(formData.dueDate).getTime() : undefined
    };

    try {
      if (!isOffline) {
        // Créer via API
        const createdTask = await createTask(newTaskData);
        setTasks(prev => [createdTask, ...prev]);
      } else {
        // Mode hors ligne
        const localTask: Task = {
          id: `local-${now}`,
          ...newTaskData
        };
        setTasks(prev => [localTask, ...prev]);
      }
      
      resetForm();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding task:', error);
      // Fallback local
      const localTask: Task = {
        id: `local-${now}`,
        ...newTaskData
      };
      setTasks(prev => [localTask, ...prev]);
      resetForm();
      setShowAddModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mettre à jour le statut d'une tâche
  const handleMoveTask = async (taskId: string, newStatus: 'todo' | 'in_progress' | 'done') => {
    if (!isOffline && !taskId.startsWith('local-')) {
      try {
        await updateTask(taskId, { status: newStatus });
        setTasks(prev => prev.map(task => 
          task.id === taskId ? { ...task, status: newStatus } : task
        ));
      } catch {
        // Fallback local
        setTasks(prev => prev.map(task => 
          task.id === taskId ? { ...task, status: newStatus } : task
        ));
      }
    } else {
      // Mode hors ligne
      setTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, status: newStatus } : task
      ));
    }
  };

  // Supprimer une tâche
  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')) return;
    
    if (!isOffline && !taskId.startsWith('local-')) {
      try {
        await deleteTask(taskId);
        setTasks(prev => prev.filter(t => t.id !== taskId));
      } catch {
        setTasks(prev => prev.filter(t => t.id !== taskId));
      }
    } else {
      setTasks(prev => prev.filter(t => t.id !== taskId));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: ''
    });
  };

  const openAddModal = (columnId: 'todo' | 'in_progress' | 'done') => {
    setSelectedColumn(columnId);
    setShowAddModal(true);
  };

  const getTasksByColumn = useCallback((columnId: string) => {
    return tasks.filter(task => task.status === columnId);
  }, [tasks]);

  if (isLoading) {
    return (
      <AdminLayout title="Kanban">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 text-[#1f75cb] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Kanban">
      {/* Alert si erreur API */}
      {apiError && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-700 font-medium">Mode hors ligne</p>
            <p className="text-xs text-amber-600">{apiError}</p>
          </div>
          <button 
            onClick={loadTasks}
            className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-100 rounded-lg transition-colors"
            title="Réessayer"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="flex flex-wrap gap-4 mb-6">
        {columns.map(column => {
          const count = getTasksByColumn(column.id).length;
          return (
            <div key={column.id} className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-[#e5e5e5]">
              <div className={`w-2 h-2 rounded-full ${column.color}`} />
              <span className="text-sm text-[#5e5e5e]">{column.title}</span>
              <span className="text-sm font-semibold text-[#1d1d1f]">{count}</span>
            </div>
          );
        })}
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {columns.map(column => {
          const columnTasks = getTasksByColumn(column.id);
          
          return (
            <div key={column.id} className="flex flex-col">
              {/* Column Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${column.color}`} />
                  <h3 className="font-medium text-[#1d1d1f]">{column.title}</h3>
                  <span className="px-2 py-0.5 bg-[#f5f5f7] rounded-full text-xs text-[#5e5e5e]">
                    {columnTasks.length}
                  </span>
                </div>
                <button
                  onClick={() => openAddModal(column.id)}
                  className="p-1.5 text-[#5e5e5e] hover:text-[#1f75cb] hover:bg-[#1f75cb]/10 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Tasks List */}
              <div className="space-y-3 min-h-[200px]">
                <AnimatePresence mode="popLayout">
                  {columnTasks.map((task, idx) => (
                    <motion.div
                      key={task.id}
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: idx * 0.05 }}
                      className="card p-4 group cursor-pointer hover:shadow-md transition-shadow"
                    >
                      {/* Priority Badge */}
                      <div className="flex items-start justify-between mb-2">
                        <span className={`text-xs px-2 py-0.5 rounded ${priorityConfig[task.priority].color}`}>
                          {priorityConfig[task.priority].label}
                        </span>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-[#5e5e5e] hover:text-red-500 hover:bg-red-50 rounded transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Title */}
                      <h4 className="font-medium text-[#1d1d1f] mb-2">{task.title}</h4>
                      
                      {/* Description */}
                      {task.description && (
                        <p className="text-sm text-[#5e5e5e] mb-3 line-clamp-2">
                          {task.description}
                        </p>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-2 border-t border-[#f0f0f0]">
                        {task.dueDate ? (
                          <div className={`flex items-center gap-1 text-xs ${
                            task.dueDate < Date.now() && column.id !== 'done' 
                              ? 'text-red-500' 
                              : 'text-[#5e5e5e]'
                          }`}>
                            <Calendar className="w-3 h-3" />
                            {new Date(task.dueDate).toLocaleDateString('fr-FR')}
                          </div>
                        ) : (
                          <div />
                        )}

                        {/* Move Buttons */}
                        <div className="flex items-center gap-1">
                          {column.id !== 'todo' && (
                            <button
                              onClick={() => handleMoveTask(task.id, column.id === 'done' ? 'in_progress' : 'todo')}
                              className="p-1 text-[#5e5e5e] hover:bg-[#f5f5f7] rounded transition-colors"
                              title="Déplacer vers la gauche"
                            >
                              <GripVertical className="w-3.5 h-3.5 rotate-90" />
                            </button>
                          )}
                          {column.id !== 'done' && (
                            <button
                              onClick={() => handleMoveTask(task.id, column.id === 'todo' ? 'in_progress' : 'done')}
                              className="p-1 text-[#5e5e5e] hover:bg-[#f5f5f7] rounded transition-colors"
                              title="Déplacer vers la droite"
                            >
                              <GripVertical className="w-3.5 h-3.5 -rotate-90" />
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Empty State */}
                {columnTasks.length === 0 && (
                  <div className="text-center py-8 text-[#5e5e5e] text-sm border-2 border-dashed border-[#e5e5e5] rounded-xl">
                    Aucune tâche
                  </div>
                )}
              </div>

              {/* Add Button at bottom */}
              <button
                onClick={() => openAddModal(column.id)}
                className="mt-3 flex items-center justify-center gap-2 w-full py-2 text-sm text-[#5e5e5e] hover:text-[#1f75cb] hover:bg-[#1f75cb]/5 rounded-lg border border-dashed border-[#e5e5e5] hover:border-[#1f75cb]/30 transition-all"
              >
                <Plus className="w-4 h-4" />
                Ajouter une tâche
              </button>
            </div>
          );
        })}
      </div>

      {/* Add Task Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50"
              onClick={() => setShowAddModal(false)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 flex items-center justify-center z-50 p-4"
            >
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e5e5]">
                  <h3 className="font-semibold text-[#1d1d1f]">Nouvelle tâche</h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="p-1 text-[#5e5e5e] hover:text-[#1d1d1f] rounded-lg hover:bg-[#f5f5f7] transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleAddTask} className="p-6 space-y-4">
                  {/* Column indicator */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#f5f5f7] rounded-lg">
                    <div className={`w-2 h-2 rounded-full ${columns.find(c => c.id === selectedColumn)?.color}`} />
                    <span className="text-sm text-[#5e5e5e]">
                      Ajout dans : {columns.find(c => c.id === selectedColumn)?.title}
                    </span>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-[#1d1d1f] mb-1.5">
                      Titre <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Nom de la tâche"
                      className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20 focus:border-[#1f75cb] transition-colors"
                      autoFocus
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-[#1d1d1f] mb-1.5">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Détails de la tâche..."
                      rows={3}
                      className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20 focus:border-[#1f75cb] transition-colors resize-none"
                    />
                  </div>

                  {/* Priority & Due Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1d1d1f] mb-1.5">
                        Priorité
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value as Task['priority'] })}
                        className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20 focus:border-[#1f75cb] transition-colors bg-white"
                      >
                        <option value="low">Basse</option>
                        <option value="medium">Moyenne</option>
                        <option value="high">Haute</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-[#1d1d1f] mb-1.5">
                        Date d'échéance
                      </label>
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                        className="w-full px-3 py-2 border border-[#e5e5e5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f75cb]/20 focus:border-[#1f75cb] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="flex-1 px-4 py-2 text-sm font-medium text-[#5e5e5e] hover:text-[#1d1d1f] hover:bg-[#f5f5f7] rounded-lg transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.title.trim()}
                      className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1f75cb] hover:bg-[#1a68b3] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Créer la tâche'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
}
